import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import { Box } from 'components/Box/Box';
import { ButtonBar } from 'components/ButtonBar/ButtonBar';
import { Divider } from 'components/Divider/Divider';
import type { FormSubmitFunction } from 'components/Form/FormContext';
import { Hide } from 'components/Hide/Hide';
import { Icon } from 'components/Icon/Icon';
import { OrgStatBadge } from 'components/OrgStatBadge/OrgStatBadge';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { BodySmall } from 'components/Text/BodySmall';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import { useTrackPageView } from 'hooks/useTrackPageView';
import {
  OrgLayoutFooterHolder,
  OrgLayoutFooterWrapper,
  OrgLayoutHeader,
  OrgLayoutSidebarLink,
  OrgLayoutSidebarLinkLi,
  OrgLayoutSidebarLinkUl,
  OrgLayoutSidebarTitle,
} from 'layouts/Org/OrgLayout.styled';
import type { ApiOrg } from 'modules/listing/api/org/types/ApiOrg';
import { ListingViewBody } from 'modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewMain } from 'modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSidebar } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSignupCta } from 'modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { orgDashboardApiPartialUpdateOrg } from 'modules/orgDashboard/api/orgDashboardApiOrg';
import type { OrgDashboardApiOrg } from 'modules/orgDashboard/api/types/OrgDashboardApiOrg';
import { SearchContextHeader } from 'modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { scrollToHash } from 'utils/scrollIntoView';
import { getSocialNetworkFromLink } from 'utils/social/getSocialNetworkFromLink';
import { iconNameBySocialNetwork } from 'utils/social/iconNameBySocialNetwork';
import { showModal } from 'zustand-stores/modalStore';
import { useUserEnvironment } from 'zustand-stores/userEnvironmentStore';

import { ViewOrgPageActionButtons } from './ViewOrgPageActionButtons';
import { ViewOrgPageBanner } from './ViewOrgPageBanner';
import { ViewOrgPageDetails } from './ViewOrgPageDetails';
import { ViewOrgPageHeader } from './ViewOrgPageHeader';
import { ViewOrgPageSidebarDetails } from './ViewOrgPageSidebarDetails';
import { ViewOrgPageSidebarHeader } from './ViewOrgPageSidebarHeader';
import { ViewOrgPageSidebarImage } from './ViewOrgPageSidebarImage';
import { ViewOrgPageSidebarTitle } from './ViewOrgPageSidebarTitle';
import { ViewOrgToolbar } from './components/ViewOrgToolbar';

type Props = {
  org: ApiOrg;
  onUpdateOrg?: (org: OrgDashboardApiOrg) => void;
  areasOfFocus?: ApiAreaOfFocus[];
  isPreview: boolean;
  isPublished: boolean;
};

export function ViewOrgPage({
  org: orgProp,
  onUpdateOrg,
  areasOfFocus,
  isPreview,
  isPublished,
}: Props) {
  const [updatedOrg, setUpdatedOrg] = useState<ApiOrg>();
  const org = updatedOrg?.id === orgProp.id ? updatedOrg : orgProp;

  const location = useLocation();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(true);
  const { user } = useUserEnvironment();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: org.id,
  });

  const orgOwner = useMemo(
    () => Boolean(user?.orgs && user.orgs.some((o) => o.id === org.id)),
    [user, org],
  );

  useTrackPageView('Org Listing Page', {
    name: org.name,
    id: org.id,
    org_type: org.orgType,
    page_type: 'Org Listings',
  });

  useSetDocumentTitle(org.name);

  useEffect(() => {
    setTimeout(() => {
      scrollToHash(location.hash.replace('section-', ''));
    }, 1);
  }, [location.hash, org]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (
      queryParams.has('admin-request') &&
      user?.emailVerified &&
      user?.firstName
    ) {
      showModal('ADMIN_REQUEST', {
        objectId: org.id,
        objectName: org.name,
      });
      queryParams.delete('admin-request');
      navigate(`${location.pathname}${queryParams}`);
    }
  }, [
    location.pathname,
    location.search,
    navigate,
    org.id,
    org.name,
    user?.emailVerified,
    user?.firstName,
  ]);

  const onSubmit: FormSubmitFunction<
    Pick<ApiOrg, 'id'> & Partial<ApiOrg>
  > = async ({ values, handleError }) => {
    try {
      const apiOrgResponse = await orgDashboardApiPartialUpdateOrg(values);
      setUpdatedOrg(apiOrgResponse);
      onUpdateOrg?.(apiOrgResponse);
    } catch (error) {
      handleError(error as Error);
    }
  };

  const socialLinks = useMemo(
    () =>
      org.links
        .map((link) => {
          const socialNetwork = getSocialNetworkFromLink(link);
          if (!socialNetwork) return;

          return { link, socialNetwork };
        })
        .filter(Boolean),
    [org.links],
  );

  const orgStat = useMemo(() => {
    let fragment;
    if (org.hiring) {
      fragment = (
        <OrgStatBadge iconName="star-check" text={getText('Actively Hiring')} />
      );
    } else if (org.hasUpcomingEvents) {
      fragment = (
        <OrgStatBadge iconName="calendar" text={getText('Upcoming Events')} />
      );
    } else if (org.seekingVolunteers) {
      fragment = (
        <OrgStatBadge iconName="group" text={getText('Seeking Volunteers')} />
      );
    }
    if (fragment) {
      return <div style={{ marginBottom: 24 }}>{fragment}</div>;
    }
    return null;
  }, [org.hasUpcomingEvents, org.hiring, org.seekingVolunteers]);

  return (
    <>
      {orgOwner && (
        <ViewOrgToolbar
          org={org as OrgDashboardApiOrg}
          isPublished={isPublished}
          editMode={editMode}
          setEditMode={setEditMode}
        />
      )}

      {((orgOwner && editMode) || org.banner) && (
        <ViewOrgPageBanner
          org={org}
          isPublished={isPublished}
          isPreview={isPreview}
          orgOwner={orgOwner}
          editMode={editMode}
          onSubmit={onSubmit}
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <BodySmall lineHeight="normal">
            <Hide xs sm md>
              <Box mb="16px">
                <ViewOrgPageSidebarImage
                  org={org}
                  orgOwner={orgOwner}
                  editMode={editMode}
                  onSubmit={onSubmit}
                />
              </Box>
            </Hide>

            {orgStat}

            <OrgLayoutSidebarTitle aria-hidden="true">
              <Hide xs sm md>
                <ViewOrgPageSidebarTitle org={org} />
              </Hide>
            </OrgLayoutSidebarTitle>

            <OrgLayoutSidebarLinkUl>
              {socialLinks.map(({ socialNetwork, link }) => (
                <OrgLayoutSidebarLinkLi key={link}>
                  <OrgLayoutSidebarLink href={link}>
                    <Icon
                      name={iconNameBySocialNetwork[socialNetwork]}
                      size={16}
                    />
                  </OrgLayoutSidebarLink>
                </OrgLayoutSidebarLinkLi>
              ))}
            </OrgLayoutSidebarLinkUl>

            <Divider marginTop={24} marginBottom={24} />

            <ViewOrgPageSidebarHeader org={org} />

            <ViewOrgPageSidebarDetails org={org} orgOwner={orgOwner} />
          </BodySmall>
        </ListingViewSidebar>

        <ListingViewMain>
          <OrgLayoutHeader>
            {searchContext && (
              <PrintHidden>
                <Box mb={[4, 4, 4, '40px']}>
                  <SearchContextHeader searchContext={searchContext} />
                </Box>
              </PrintHidden>
            )}

            <ViewOrgPageHeader org={org} isPreview={isPreview} />
          </OrgLayoutHeader>

          <ViewOrgPageDetails org={org} areasOfFocus={areasOfFocus} />

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!isPreview && !user && (
        <PrintHidden>
          <OrgLayoutFooterHolder>
            <OrgLayoutFooterWrapper>
              <ListingViewSignupCta
                listingUrl={org.url[CURRENT_LOCALE()]}
                type="ORG"
              />
            </OrgLayoutFooterWrapper>
          </OrgLayoutFooterHolder>
        </PrintHidden>
      )}

      <ButtonBar>
        <ViewOrgPageActionButtons org={org} isPreview={isPreview} mobile />
      </ButtonBar>
    </>
  );
}
